import React, { useState, useEffect, useCallback, useRef } from 'react';
import joursOuvresApi from '../services/jours-ouvres';
import './JoursOuvresAdmin.css';

// Administration ▸ Jours ouvrés (super-admin) — calendrier par territoire de la
// table `jours_ouvres` : un clic sur un jour le passe d'ouvré à fermé.
// Repris de l'onglet Budget du Reporting Financier, supprimé le 2026-10-26 :
// la table sert au Planning (jours fermés : fériés d'un territoire, lundi au
// vendredi), à la prise de RDV publique, au Remplissage atelier et aux
// concessions. Elle doit être importée chaque année (une ligne par
// territoire et par jour).

const MOIS_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const JOURS_SEMAINE = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

// 'YYYY-MM-DD' en date LOCALE : `new Date('2026-06-21')` est lue en UTC minuit
// et recule d'un jour aux Antilles (UTC-4), ce qui décale toute la grille.
const parseLocalDate = (s) => {
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

function JoursOuvresAdmin() {
  const [data, setData] = useState([]);
  const [dimensions, setDimensions] = useState({ departements: [], annees: [] });
  const [departement, setDepartement] = useState('');
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ mensuel: [], totaux: [] });
  const [message, setMessage] = useState(null); // { ton: 'ok' | 'ko', texte }
  const fileInputRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { annee };
      if (departement) params.departement = departement;
      const [joursRes, dimsRes, countsRes] = await Promise.all([
        joursOuvresApi.getAll(params),
        joursOuvresApi.getDimensions(),
        joursOuvresApi.getCount(params),
      ]);
      setData(joursRes.data.data || []);
      setDimensions(dimsRes.data || { departements: [], annees: [] });
      setCounts(countsRes.data || { mensuel: [], totaux: [] });
    } catch (err) {
      setMessage({ ton: 'ko', texte: 'Les jours ouvrés n’ont pas pu être chargés.' });
    } finally {
      setLoading(false);
    }
  }, [annee, departement]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await joursOuvresApi.importer(formData);
      setMessage({ ton: 'ok', texte: `${res.data.inserted} jours importés.` });
      loadData();
    } catch (err) {
      setMessage({ ton: 'ko', texte: `Import refusé : ${err.response?.data?.error || err.message}` });
    }
    e.target.value = '';
  }

  async function handleToggle(jour) {
    try {
      await joursOuvresApi.basculer(jour.id, !jour.est_ouvre);
      setData(prev => prev.map(d => d.id === jour.id ? { ...d, est_ouvre: !d.est_ouvre } : d));
      const params = { annee };
      if (departement) params.departement = departement;
      const countsRes = await joursOuvresApi.getCount(params);
      setCounts(countsRes.data || { mensuel: [], totaux: [] });
    } catch (err) {
      setMessage({ ton: 'ko', texte: 'Le jour n’a pas pu être modifié.' });
    }
  }

  // Un seul territoire affiché à la fois (le premier par défaut)
  const deptFilter = departement || (dimensions.departements.length > 0 ? dimensions.departements[0] : '');
  const filteredData = deptFilter ? data.filter(d => d.departement === deptFilter) : data;

  const moisData = {};
  for (const jour of filteredData) {
    const m = parseLocalDate(jour.date).getMonth();
    if (!moisData[m]) moisData[m] = [];
    moisData[m].push(jour);
  }
  const totalOuvres = (counts.totaux || []).find(t => !deptFilter || t.departement === deptFilter);
  const derniereAnnee = dimensions.annees.length ? Math.max(...dimensions.annees) : null;

  return (
    <div className="jo-admin">
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />

      <div className="jo-entete">
        <div>
          <h3>Jours ouvrés par territoire</h3>
          <p className="jo-aide">
            Utilisés par le Planning (jours fériés fermés), la prise de rendez-vous en ligne et le Remplissage atelier.
            Un clic sur un jour le passe d'ouvré à fermé.
            {derniereAnnee && derniereAnnee <= new Date().getFullYear() && (
              <> <strong>Calendrier saisi jusqu'en {derniereAnnee}</strong> : importez l'année suivante avant le 1er janvier.</>
            )}
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => fileInputRef.current?.click()}>Importer un fichier Excel</button>
      </div>
      <p className="jo-format">Format attendu : une ligne par jour, colonnes territoire, date (JJ/MM/AAAA), ouvré (1) ou fermé (0), avec une ligne d'en-tête.</p>

      {message && (
        <div className={`jo-message jo-message--${message.ton}`} role="status">
          {message.texte}
          <button type="button" className="jo-message-fermer" onClick={() => setMessage(null)} aria-label="Fermer le message">✕</button>
        </div>
      )}

      <div className="page-toolbar">
        <select className="page-select" value={annee} onChange={e => setAnnee(parseInt(e.target.value, 10))}>
          {(dimensions.annees.length > 0 ? dimensions.annees : [new Date().getFullYear()]).map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select className={`page-select ${departement ? 'active-filter' : ''}`} value={departement} onChange={e => setDepartement(e.target.value)}>
          {dimensions.departements.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        {totalOuvres && (
          <span className="jo-total">Total : <strong>{totalOuvres.total}</strong> jours ouvrés</span>
        )}
      </div>

      {loading ? (
        <div className="jo-vide">Chargement…</div>
      ) : filteredData.length === 0 ? (
        <div className="jo-vide">
          <p>Aucun jour saisi pour {annee}.</p>
          <p>Importez un fichier Excel pour cette année.</p>
        </div>
      ) : (
        <div className="jo-calendrier">
          {Array.from({ length: 12 }, (_, mi) => {
            const jours = moisData[mi] || [];
            const countMois = (counts.mensuel || []).find(c => c.mois === mi + 1 && (!deptFilter || c.departement === deptFilter));
            const firstDay = jours.length > 0 ? parseLocalDate(jours[0].date).getDay() : 0;
            return (
              <div key={mi} className="jo-mois">
                <div className="jo-mois-entete">
                  <span className="jo-mois-nom">{MOIS_LABELS[mi]} {annee}</span>
                  <span className="jo-mois-nb">{countMois?.jours_ouvres || 0} j</span>
                </div>
                <div className="jo-semaine">
                  {JOURS_SEMAINE.map(j => <span key={j}>{j[0]}</span>)}
                </div>
                <div className="jo-jours">
                  {Array.from({ length: firstDay }, (_, i) => <span key={`pad-${i}`} className="jo-jour jo-vide-case" />)}
                  {jours.map(jour => {
                    const d = parseLocalDate(jour.date);
                    const weekend = d.getDay() === 0 || d.getDay() === 6;
                    return (
                      <button
                        type="button"
                        key={jour.id}
                        className={`jo-jour ${jour.est_ouvre ? 'jo-ouvre' : 'jo-ferme'}${weekend ? ' jo-weekend' : ''}`}
                        onClick={() => handleToggle(jour)}
                        title={`${d.getDate()}/${mi + 1} — ${jour.est_ouvre ? 'Ouvré' : 'Fermé'} (clic pour changer)`}
                      >
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default JoursOuvresAdmin;
